import { getLocalizedValue } from "@repo/shared";
import {
  type FinancialSituationStatusDef,
  type FsClientType,
  fsShowsHouseholdRatios,
} from "@repo/shared/types";
import { Badge } from "@/components/ui/badge";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./summary-tab.i18n";
import { formatCurrency, formatMultiple, formatPercent } from "./utils";

/** QL-39 — the type-neutral ratios; liquid net worth and capacity for loss went with the field `type`. */
type Metrics = {
  netWorth: number;
  debtToIncomeRatio: number;
  monthlyDebtServiceRatio: number;
  savingsRate: number;
};

type SummaryTabProps = {
  metrics: Metrics;
  currency: string;
  lang: string;
  showSummaryMetrics: boolean;
  /**
   * QP-176 — Company sessions keep the type-neutral figures (net worth,
   * derived statuses) but suppress the person-suitability math (income
   * ratios). QL-140 — the raw client type; Group keeps the household math (a
   * group of natural persons IS a household).
   */
  clientType?: FsClientType;
  /** QP-195 — server-derived statuses from calculatedData, keyed by status def key. */
  derivedStatuses?: Record<string, string>;
  statusDefs?: FinancialSituationStatusDef[];
};

/** Capitalize the first letter for display ("affluent" → "Affluent"). */
function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

export function SummaryTab({
  metrics,
  currency,
  lang,
  showSummaryMetrics,
  clientType = "Person",
  derivedStatuses = {},
  statusDefs = [],
}: SummaryTabProps) {
  const { tenantDefaultLanguage } = useUserLanguage();
  const { formatNumber } = useRegionalFormat();
  const t = useT(T);

  // QP-176 — the ratio metrics assume a personal household (annual income);
  // hide them for Company sessions rather than showing misleading numbers.
  // QL-140 — deliberately shown for Group: a group of natural persons is a
  // household, so the same math applies (only Company is excluded).
  const showPersonMath = fsShowsHouseholdRatios(clientType);

  const statusEntries = Object.entries(derivedStatuses);

  return (
    <div className="space-y-6">
      {statusEntries.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="derived-statuses">
          {statusEntries.map(([key, value]) => {
            const def = statusDefs.find((d) => d.key === key);
            const label = def
              ? getLocalizedValue(def.labelI18n, lang, tenantDefaultLanguage) || key
              : key;
            return (
              <Badge key={key} variant="secondary">
                {label}: {capitalize(value)}
              </Badge>
            );
          })}
        </div>
      )}

      {showSummaryMetrics && (
        <div className="space-y-4">
          <h4 className="text-base font-medium">{t.financialSummary}</h4>
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="text-sm text-muted-foreground">{t.netWorth}</div>
            <div className={`text-xl font-bold ${metrics.netWorth < 0 ? "text-destructive" : ""}`}>
              {formatCurrency(metrics.netWorth, currency, formatNumber)}
            </div>
          </div>

          {showPersonMath && (
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm">{t.debtToIncomeRatio}</span>
                <span className="font-medium">
                  {formatMultiple(metrics.debtToIncomeRatio, formatNumber)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm">{t.monthlyDebtServiceRatio}</span>
                <span className="font-medium">
                  {formatPercent(metrics.monthlyDebtServiceRatio, formatNumber)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm">{t.savingsRate}</span>
                <span className="font-medium">
                  {formatPercent(metrics.savingsRate, formatNumber)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
